import { useMemo } from 'react';
import { useChannels } from './useChannels';
import { ChannelTypeEnum, ChannelListItemResponse } from '../types/backend';

/**
 * useAllChannels - Fetches all channel types (internal, client, dm) and merges them
 *
 * Since the backend API requires a type parameter, this hook makes 3 parallel queries
 * and combines the results into a single list sorted by last-message-at.
 */
export const useAllChannels = ({
  section = 'active',
  initialLimit = 5,
  nextLimit = 15,
}: {
  section?: 'active' | 'archived';
  initialLimit?: number;
  nextLimit?: number;
} = {}) => {
  // Fetch all three channel types in parallel
  // Disable autoFetch here to prevent triggering multiple full background
  // fetch loops in parallel. Aggregating three channel streams would otherwise
  // multiply requests when autoFetch is enabled.
  const internalChannels = useChannels({
    type: ChannelTypeEnum.INTERNAL,
    section,
    initialLimit,
    nextLimit,
    autoFetch: false,
  });

  const clientChannels = useChannels({
    type: ChannelTypeEnum.CLIENT,
    section,
    initialLimit,
    nextLimit,
  });

  const dmChannels = useChannels({
    type: ChannelTypeEnum.DM,
    section,
    initialLimit,
    nextLimit,
  });

  // Combine loading states
  const isLoading =
    internalChannels.isLoading ||
    clientChannels.isLoading ||
    dmChannels.isLoading;

  // Combine error states
  const isError =
    internalChannels.isError || clientChannels.isError || dmChannels.isError;

  const error =
    internalChannels.error || clientChannels.error || dmChannels.error;

  // Merge all pages from all queries
  const mergedData = useMemo(() => {
    const internalPages = internalChannels.data?.pages || [];
    const clientPages = clientChannels.data?.pages || [];
    const dmPages = dmChannels.data?.pages || [];

    // Flatten all rows from all pages
    const allChannels: ChannelListItemResponse[] = [
      ...internalPages.flatMap((page) => page.rows),
      ...clientPages.flatMap((page) => page.rows),
      ...dmPages.flatMap((page) => page.rows),
    ];

    // Sort by last-message-at (most recent first), then by name
    allChannels.sort((a, b) => {
      // If both have last-message-at, sort by that
      if (a['last-message-at'] && b['last-message-at']) {
        return (
          new Date(b['last-message-at']).getTime() -
          new Date(a['last-message-at']).getTime()
        );
      }

      // If only one has last-message-at, prioritize it
      if (a['last-message-at']) return -1;
      if (b['last-message-at']) return 1;

      // If neither has messages, sort alphabetically by name
      const aName = a.name || '';
      const bName = b.name || '';
      return aName.localeCompare(bName);
    });

    // Calculate total count
    const totalCount =
      (internalPages[0]?.count || 0) +
      (clientPages[0]?.count || 0) +
      (dmPages[0]?.count || 0);

    return {
      pages: [{ rows: allChannels, count: totalCount }],
      pageParams: [0],
    };
  }, [internalChannels.data, clientChannels.data, dmChannels.data]);

  // Combine hasNextPage - true if ANY query has more pages
  const hasNextPage =
    internalChannels.hasNextPage ||
    clientChannels.hasNextPage ||
    dmChannels.hasNextPage;

  // fetchNextPage for all queries
  const fetchNextPage = async () => {
    await Promise.all([
      internalChannels.hasNextPage
        ? internalChannels.fetchNextPage()
        : Promise.resolve(),
      clientChannels.hasNextPage
        ? clientChannels.fetchNextPage()
        : Promise.resolve(),
      dmChannels.hasNextPage ? dmChannels.fetchNextPage() : Promise.resolve(),
    ]);
  };

  return {
    data: mergedData,
    isLoading,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    // Expose individual queries for refetching if needed
    queries: {
      internal: internalChannels,
      client: clientChannels,
      dm: dmChannels,
    },
  };
};
